import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/layout/AuthLayout';
import { BRAND, fieldSx, primaryButtonSx } from '../styles/ui';

const Login: React.FC = () => {
  const { login, error, isLoading, isAuthenticated } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; error?: string } | undefined;
  // Handle redirects and back navigation
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile');
      return;
    }

    // Check for verification back navigation
    const fromVerify = locationState?.from === 'verify-account';
    const pendingEmail = sessionStorage.getItem('pendingVerificationEmail');
    
    if (fromVerify && pendingEmail) {
      // If coming back from verify page with a pending email, keep it in the form
      formik.setFieldValue('username', pendingEmail);
      setLoginError('Please verify your account. Check your email for the verification code.');
      // Clear the pending email from session storage
      sessionStorage.removeItem('pendingVerificationEmail');
    } else if (locationState?.error) {
      // Show any error messages from navigation state
      setLoginError(locationState.error);
    }
  }, [isAuthenticated, navigate, location.state]);

  // Form validation schema
  const validationSchema = Yup.object({
    username: Yup.string()
      .email('Enter a valid email')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      username: '',
      password: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoginError(null);
      try {
        const response = await login(values.username, values.password);
        
        // If login returns a challenge, handle it
        if (response?.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          window.location.href = `/first-time-login?username=${encodeURIComponent(values.username)}&session=${encodeURIComponent(response.Session)}`;
          return;
        }
      } catch (err: any) {
        console.error('Login error:', err);
        
        if (err.name === 'UserNotConfirmedException' || err.code === 'UserNotConfirmedException') {
          console.log('User not confirmed, redirecting to verification...');
          // Store email in session storage for verification
          sessionStorage.setItem('pendingVerificationEmail', values.username);
          // Use navigate instead of window.location to maintain React Router state
          navigate(`/verify-account?email=${encodeURIComponent(values.username)}`, {
            state: { email: values.username }
          });
          return;
        }
        
        // Handle other errors
        if (err.name === 'NotAuthorizedException' || err.code === 'NotAuthorizedException') {
          setLoginError('Incorrect username or password');
        } else {
          setLoginError(err.message || 'Login failed. Please try again.');
        }
      }
    }
  });

  return (
    <AuthLayout
      title="Sign In"
      footerStart={
        <Link
          component={RouterLink}
          to="/register"
          sx={{ color: BRAND.ink, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Don't have an account? Sign Up
        </Link>
      }
      footerActions={
        <Button
          type="submit"
          form="login-form"
          variant="contained"
          disabled={isLoading}
          sx={primaryButtonSx}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>
      }
    >
      {(loginError || error) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loginError || error}
        </Alert>
      )}

      <form id="login-form" onSubmit={formik.handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="username"
          label="Email Address"
          name="username"
          type="email"
          autoComplete="email"
          autoFocus
          value={formik.values.username}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldTouched('username', true, false);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.username && Boolean(formik.errors.username)}
          helperText={formik.touched.username && formik.errors.username ? String(formik.errors.username) : ''}
          disabled={isLoading}
          sx={fieldSx}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={formik.values.password}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldTouched('password', true, false);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password ? String(formik.errors.password) : ''}
          disabled={isLoading}
          sx={fieldSx}
        />
      </form>
    </AuthLayout>
  );
};

export default Login;
